import { useEffect, useState } from "react";

import { EMBED_DEFAULT_HEIGHT, EMBED_DEFAULT_WIDTH } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { AlignmentButtons } from "@/components/doc/right-sidebar/alignment-buttons/AlignmentButtons";
import { Input } from "@/components/input/Input";
import { InputSize } from "@/components/input/InputSize";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";

import type { DocumentModel } from "@/models/DocumentModel";

export const EmbedStyle = observer(function EmbedStyle({ doc }: { doc: DocumentModel }) {
  const t = useTranslations("sidebar.blocks");
  const tEmbed = useTranslations("sidebar.embedStyle");
  const tToolbars = useTranslations("sidebar.toolbars");
  const { tiptap, editorState } = doc.editor;
  const { embedAttrs } = editorState;

  const currentUrl = embedAttrs?.url || "";
  const currentWidth = embedAttrs?.width ?? EMBED_DEFAULT_WIDTH;
  const currentHeight = embedAttrs?.height ?? EMBED_DEFAULT_HEIGHT;

  const [url, setUrl] = useState(currentUrl);

  useEffect(() => {
    setUrl(currentUrl);
  }, [currentUrl]);

  if (!tiptap || !editorState.isEmbed) {
    return null;
  }

  const updateEmbedUrl = () => {
    if (url !== currentUrl) {
      tiptap.chain().setEmbedUrl(url).run();
    }
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      updateEmbedUrl();
    }
  };

  const handleWidthChange = (newWidth: number) => {
    tiptap.chain().setEmbedWidth(newWidth).run();
  };

  const handleHeightChange = (newHeight: number) => {
    tiptap.chain().setEmbedHeight(newHeight).run();
  };

  return (
    <ActionsDisclosure
      data-panel="embed_style"
      title={t("embed.title")}
      disclosureProps={{ defaultOpen: true }}
    >
      <div className="flex flex-1 items-center justify-between">
        <Input
          value={url}
          placeholder="https://"
          onChange={(e) => setUrl(e.target.value.trim())}
          onBlur={updateEmbedUrl}
          onKeyDown={handleUrlKeyDown}
          onCrossClick={() => setUrl("")}
        />
      </div>
      <div className="flex flex-1 items-center justify-between gap-1 p-1.25">
        <InputSize
          value={currentWidth}
          type="width"
          onChange={handleWidthChange}
          label={tEmbed("embedWidth")}
        />
        <InputSize
          value={currentHeight}
          type="height"
          onChange={handleHeightChange}
          label={tEmbed("embedHeight")}
        />
        <SidebarToolbar label={tToolbars("embed")}>
          <AlignmentButtons doc={doc} />
        </SidebarToolbar>
      </div>
    </ActionsDisclosure>
  );
});
